import dynamic from 'next/dynamic';
import { useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import { useI18nFormatting } from 'hooks/use-i18n-formatting';
import { formatRatio } from 'utils/format-ratio';
import { capitalizeFirst } from 'utils/capitalize-first';
import { Namespaces } from 'types/locales';
import type {
  MarketHealthData,
  MarketVolumeData,
  PriceTrendPoint,
  PropertyTypeSegment,
} from 'components/home-page/local-focus/market-dynamics';
import type { AtAGlanceStats } from 'components/home-page/local-focus/at-a-glance';
import type { CityInsightsData } from 'components/home-page/local-focus/city-insights';
import type { InsightsListingType } from 'data/insights/types';
import styles from './insights-pane.module.scss';

const AtAGlance = dynamic(() => import('components/home-page/local-focus/at-a-glance'), { ssr: false });
const MarketDynamics = dynamic(() => import('components/home-page/local-focus/market-dynamics'), { ssr: false });
const CityInsights = dynamic(() => import('components/home-page/local-focus/city-insights'), { ssr: false });

export type AreaPageV2InsightsContent = {
  locationName: string;
  atAGlanceStats: AtAGlanceStats | null;
  propertyTypes: PropertyTypeSegment[];
  marketVolume: MarketVolumeData | null;
  priceTrends: PriceTrendPoint[];
  marketHealth: MarketHealthData | undefined;
  cityInsights: CityInsightsData | null;
  isCityInsightsGenerating: boolean;
  /** Market the stats describe, from the applied rental/sold filters. */
  listingType: InsightsListingType;
};

export type InsightsPaneProps = {
  content: AreaPageV2InsightsContent;
};

// Sentinel wrapped around metric values while assembling the localized strings.
// renderWithBold() splits on it and emits <strong> for the marked spans, so we can
// bold interpolated metrics without embedding markup in the translation files.
const BOLD_MARK = '\u0000';

const bold = (value: string) => `${BOLD_MARK}${value}${BOLD_MARK}`;

/** Translation key suffix for listing-type variants: `heading`, `heading_rent`, `heading_sold`. */
const variantSuffix = (listingType: InsightsListingType) => (listingType === 'sale' ? '' : `_${listingType}`);

/** English defaults for the heading, as [with location, without location]. */
const HEADING_DEFAULTS: Record<InsightsListingType, [string, string]> = {
  sale: ['{{location}} Real Estate Market Insights', 'Real Estate Market Insights'],
  rent: ['{{location}} Rental Market Insights', 'Rental Market Insights'],
  sold: ['{{location}} Sold Homes Market Insights', 'Sold Homes Market Insights'],
};

const MARKET_DYNAMICS_DESCRIPTION_DEFAULTS: Record<InsightsListingType, string> = {
  sale: "How {{location}}'s housing market breaks down by property type, price range, and overall health for buyers, sellers, and agents tracking the local market.",
  rent: "How {{location}}'s rental market breaks down by property type, rent range, and leasing pace for renters, landlords, and agents tracking the local market.",
  sold: "How {{location}}'s recent home sales break down by property type, sold price range, and pace of sale over the last 90 days.",
};

type IntroCopy = {
  /** Translation namespace under `area_page_v2` for this market's intro sentences. */
  namespace: string;
  /** Interpolation names the lead sentences use for the average price and the count. */
  priceParam: string;
  countParam: string;
  /** Key prefix of the pace sentences and the interpolation name of their ratio. */
  pacePrefix: string;
  ratioParam: string;
  dataUpdated: string;
  leadFull: string;
  leadPrice: string;
  leadListings: string;
  types: Record<'house' | 'condo' | 'townhouse', string>;
  paceFull: string;
  paceDays: string;
  paceRatio: string;
};

/** English defaults and interpolation names for the intro summary, per market. */
const INTRO_COPY: Record<InsightsListingType, IntroCopy> = {
  sale: {
    namespace: 'area_page_v2.insights_intro',
    priceParam: 'avgListingPrice',
    countParam: 'activeListings',
    pacePrefix: 'selling',
    ratioParam: 'sellListRatio',
    dataUpdated: 'Data updated {{date}} · Sourced from active MLS & eXp Realty listings',
    leadFull: 'The average home in {{location}} currently lists for {{avgListingPrice}}, with {{activeListings}} active listings on the market.',
    leadPrice: 'The average home in {{location}} currently lists for {{avgListingPrice}}.',
    leadListings: '{{location}} has {{activeListings}} active listings on the market.',
    types: {
      house: 'detached houses average {{price}}',
      condo: 'condos average {{price}}',
      townhouse: 'townhouses average {{price}}',
    },
    paceFull: 'Homes are selling in about {{daysOnMarket}} days at {{sellListRatio}} of asking price.',
    paceDays: 'Homes are selling in about {{daysOnMarket}} days.',
    paceRatio: 'Homes are selling at {{sellListRatio}} of asking price.',
  },
  rent: {
    namespace: 'area_page_v2.insights_intro_rent',
    priceParam: 'avgRent',
    countParam: 'activeRentals',
    pacePrefix: 'leasing',
    ratioParam: 'listRatio',
    dataUpdated: 'Data updated {{date}} · Sourced from active and recently leased MLS & eXp Realty rentals',
    leadFull: 'The average rental in {{location}} is listed at {{avgRent}} per month, with {{activeRentals}} rentals on the market.',
    leadPrice: 'The average rental in {{location}} is listed at {{avgRent}} per month.',
    leadListings: '{{location}} has {{activeRentals}} rentals on the market.',
    types: {
      house: 'houses rent for an average of {{price}}',
      condo: 'condos rent for an average of {{price}}',
      townhouse: 'townhouses rent for an average of {{price}}',
    },
    paceFull: 'Rentals are leasing in about {{daysOnMarket}} days at {{listRatio}} of asking rent.',
    paceDays: 'Rentals are leasing in about {{daysOnMarket}} days.',
    paceRatio: 'Rentals are leasing at {{listRatio}} of asking rent.',
  },
  sold: {
    namespace: 'area_page_v2.insights_intro_sold',
    priceParam: 'avgSoldPrice',
    countParam: 'soldCount',
    pacePrefix: 'selling',
    ratioParam: 'sellListRatio',
    dataUpdated: 'Data updated {{date}} · Sourced from MLS sales closed in the last 90 days',
    leadFull: 'Homes in {{location}} sold for an average of {{avgSoldPrice}} over the last 90 days, across {{soldCount}} sales.',
    leadPrice: 'Homes in {{location}} sold for an average of {{avgSoldPrice}} over the last 90 days.',
    leadListings: '{{location}} recorded {{soldCount}} home sales over the last 90 days.',
    types: {
      house: 'detached houses sold for an average of {{price}}',
      condo: 'condos sold for an average of {{price}}',
      townhouse: 'townhouses sold for an average of {{price}}',
    },
    paceFull: 'Homes sold in about {{daysOnMarket}} days at {{sellListRatio}} of asking price.',
    paceDays: 'Homes sold in about {{daysOnMarket}} days.',
    paceRatio: 'Homes sold at {{sellListRatio}} of asking price.',
  },
};

function renderWithBold(text: string) {
  return text
    .split(BOLD_MARK)
    .map((segment, i) => (i % 2 === 1 ? <strong key={i}>{segment}</strong> : segment));
}

export function InsightsPane({ content }: InsightsPaneProps) {
  const { t } = useTranslation(Namespaces.COMMON);
  const { formatCurrency, formatNumber, formatDate, locale } = useI18nFormatting();
  const {
    locationName,
    atAGlanceStats,
    propertyTypes,
    marketVolume,
    priceTrends,
    marketHealth,
    cityInsights,
    isCityInsightsGenerating,
    listingType,
  } = content;
  const variant = variantSuffix(listingType);

  const hasMarketDynamics =
    propertyTypes.length > 0 || Boolean(marketVolume) || priceTrends.length > 2 || Boolean(marketHealth);

  // City name without the trailing province/state code (e.g. "Toronto, ON" -> "Toronto").
  const cityName = locationName.split(',')[0].trim();

  const marketDynamicsDescription = cityName
    ? t(`area_page_v2.market_dynamics_description${variant}`, {
      location: cityName,
      defaultValue: MARKET_DYNAMICS_DESCRIPTION_DEFAULTS[listingType],
    })
    : undefined;

  const cityInsightsDescription = cityName
    ? t('area_page_v2.city_insights_description', {
      location: cityName,
      defaultValue:
        'Neighbourhood context beyond price: walkability, transit, amenities, and daily life in {{location}}.',
    })
    : undefined;

  // Build the intro summary from only the stats that are actually present (> 0),
  // so we never render sentences like "condos average $0" or "about 0 days".
  const summarySentences = useMemo(() => {
    if (!atAGlanceStats) return [];

    const stats = atAGlanceStats;
    const copy = INTRO_COPY[listingType];
    const sentences: string[] = [];

    const hasPrice = stats.avgHomePrice > 0;
    const hasListings = stats.totalListings > 0;
    const leadValues = {
      location: cityName,
      [copy.priceParam]: bold(formatCurrency(stats.avgHomePrice)),
      [copy.countParam]: bold(formatNumber(stats.totalListings)),
    };
    if (hasPrice && hasListings) {
      sentences.push(t(`${copy.namespace}.lead_full`, { ...leadValues, defaultValue: copy.leadFull }));
    } else if (hasPrice) {
      sentences.push(t(`${copy.namespace}.lead_price`, { ...leadValues, defaultValue: copy.leadPrice }));
    } else if (hasListings) {
      sentences.push(t(`${copy.namespace}.lead_listings`, { ...leadValues, defaultValue: copy.leadListings }));
    }

    const typeFragments: string[] = [];
    const typePrices: [keyof typeof copy.types, number][] = [
      ['house', stats.avgHousePrice],
      ['condo', stats.avgCondoPrice],
      ['townhouse', stats.avgTownhousePrice],
    ];
    for (const [type, price] of typePrices) {
      if (price > 0) {
        typeFragments.push(
          t(`${copy.namespace}.type_${type}`, {
            price: bold(formatCurrency(price)),
            defaultValue: copy.types[type],
          })
        );
      }
    }
    if (typeFragments.length > 0) {
      const joined = new Intl.ListFormat(locale, { style: 'long', type: 'conjunction' }).format(typeFragments);
      sentences.push(`${capitalizeFirst(joined, locale)}.`);
    }

    const hasDays = stats.daysOnMarket > 0;
    const hasRatio = stats.sellListRatio > 0;
    const paceValues = {
      daysOnMarket: bold(formatNumber(stats.daysOnMarket)),
      [copy.ratioParam]: bold(formatRatio(stats.sellListRatio)),
    };
    if (hasDays && hasRatio) {
      sentences.push(t(`${copy.namespace}.${copy.pacePrefix}_full`, { ...paceValues, defaultValue: copy.paceFull }));
    } else if (hasDays) {
      sentences.push(t(`${copy.namespace}.${copy.pacePrefix}_days`, { ...paceValues, defaultValue: copy.paceDays }));
    } else if (hasRatio) {
      sentences.push(t(`${copy.namespace}.${copy.pacePrefix}_ratio`, { ...paceValues, defaultValue: copy.paceRatio }));
    }

    if (sentences.length > 0) {
      sentences.push(t('area_page_v2.insights_intro.breakdown', { defaultValue: "Here's the full breakdown:" }));
    }

    return sentences;
  }, [atAGlanceStats, cityName, listingType, locale, formatCurrency, formatNumber, t]);

  return (
    <section className={styles.root} data-testid="AreaPageV2-InsightsPane" aria-label="Insights">
      <div className={styles.scroll}>
        <p className={styles.eyebrow}>
          {t('area_page_v2.heading_eyebrow', { defaultValue: 'Local Market Report' })}
        </p>
        <h2 className={styles.heading}>
          {locationName
            ? t(`area_page_v2.heading${variant}`, {
              location: locationName,
              defaultValue: HEADING_DEFAULTS[listingType][0],
            })
            : t(`area_page_v2.heading_no_location${variant}`, { defaultValue: HEADING_DEFAULTS[listingType][1] })}
        </h2>

        {/*
          No `locationUrl` is handed to the sections below. Each of them links its heading's
          location only when given one, and this tab is already that area's page — the link
          went nowhere new and only underlined the location on hover.
        */}
        <div className={styles.sections}>
          {atAGlanceStats && (
            <div className={styles.intro}>
              <p className={styles.introMeta}>
                {renderWithBold(
                  t(`${INTRO_COPY[listingType].namespace}.data_updated`, {
                    date: bold(formatDate(new Date(), 'short')),
                    defaultValue: INTRO_COPY[listingType].dataUpdated,
                  })
                )}
              </p>
              {summarySentences.length > 0 && (
                <p className={styles.introSummary}>{renderWithBold(summarySentences.join(' '))}</p>
              )}
            </div>
          )}

          {atAGlanceStats && (
            <AtAGlance locationName={locationName} stats={atAGlanceStats} hideHeading listingType={listingType} />
          )}

          {hasMarketDynamics && (
            <MarketDynamics
              locationName={locationName}
              propertyTypes={propertyTypes}
              volume={marketVolume}
              priceTrends={priceTrends.length > 2 ? priceTrends : undefined}
              marketHealth={marketHealth}
              description={marketDynamicsDescription}
              listingType={listingType}
            />
          )}

          {(cityInsights || isCityInsightsGenerating) && (
            <CityInsights
              locationName={locationName}
              data={cityInsights}
              isGenerating={isCityInsightsGenerating && !cityInsights}
              description={cityInsightsDescription}
            />
          )}
        </div>
      </div>
    </section>
  );
}
